<template>
  <div class="FieldSet">
    <!-- @ Overste Meesters: In asset create form hebben we collapsible (toggle) en isActive nodig anders
           werkt gridOperatorAddress niet en o.d.m heb ik geen beter idee om dit volledig generiek te maken.
           Dit gaan we nog later goed doen.
    -->
    <div v-if="collapsible">
      <div class="d-flex">
        <Toggle
          v-if="path"
          :value="isActive"
          class="mr-3 mb-2"
          @change="value => $emit('change', { value, path })"
        />
        <FieldSetHeader
          v-if="getPropValue('label') && showFieldSetHeader"
          :rank="5"
          :title="getPropValue('label')"
        />
      </div>
      <div
        v-if="isActive"
        :class="{ 'ml-5': path }"
      >
        <Row
          v-for="(row, index) in rows"
          :key="`row_${index}`"
          :fields="row.fields"
          :columns="row.columns || 'auto'"
          v-bind="$props"
          @blur="handleBlur"
          @input="handleInput"
        />
      </div>
    </div>
    <div v-else>
      <div class="d-flex align-items-center">
        <FieldSetHeader
          v-if="getPropValue('label') && showFieldSetHeader"
          :rank="5"
          :title="getPropValue('label')"
        />
        <i
          v-if="hasInfo"
          v-b-tooltip.hover
          :title="getPropValue('info')"
          class="fal fa-info-circle mb-1 ml-2"
        />
      </div>
      <Row
        v-for="(row, index) in rows"
        :key="`row_${index}`"
        :fields="row.fields"
        :columns="row.columns || 'auto'"
        v-bind="$props"
        @blur="handleBlur"
        @input="handleInput"
      />
    </div>
  </div>
</template>

<script>
import FieldSetHeader from '@/components/form/FieldSetHeader'
import Row from '@/components/form/Row'
import Toggle from '@/components/common/Toggle'
import * as dynamicFormHelpers from '@/services/dynamicFormHelpers'
import { default as fn } from '@/helpers/recordList'
import { evaluateCondition, valuesLookup } from '@/helpers/evaluateCondition'
import dot from 'dot-object'
import { deriveEvse, deriveEvseId, deriveEvseId2 } from '@/services/validation'

export default {
  name: 'CustomFieldSet',
  components: { FieldSetHeader, Row, Toggle },
  props: {
    // The optional fieldset header
    label: {
      type: [String, Object],
      default: '',
    },
    showFieldSetHeader: {
      type: Boolean,
      default: true,
    },
    uuid: {
      type: String,
      default: '',
    },
    // The fields, along with their properties
    fields: {
      type: Object,
      required: true,
    },
    // Record
    record: {
      type: Object,
      default: () => { },
    },
    // The field values
    values: {
      type: Object,
      default: () => { },
    },
    // The field validation rules (Vuelidate rules)
    validation: {
      type: Object,
      default: () => { },
    },
    info: {
      type: [String, Object],
      default: '',
    },
    // Use bootstrap's debounce timeout in ms. to avoid to many emits
    debounce: {
      type: [Number, String],
      default: '0',
    },
    // The fieldset layout
    layout: {
      type: Array,
      required: true,
    },
    // The nesting structure of the Fieldset, using dot & block notation (e.g. 'parent.child[0].grandchild')
    path: {
      type: String,
      default: '',
    },
    /**
     * Whether the fields are disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    locked: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether the step this fieldset belongs to is the process's current step.
     *  Fields with lockedUnlessCurrentStep are locked otherwise, also during correction / change.
     */
    isCurrentStep: {
      type: Boolean,
      default: true,
    },

    /**
     * Viewer mode
     */
    viewer: {
      type: Boolean,
      default: false,
    },

    canReset: {
      type: Boolean,
      default: true,
    },

    plannedDateAlert: {
      type: String,
      default: '',
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: ''
    },
    groupClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      rows: []
    }
  },
  computed: {
    hasInfo() {
      return this.info
    },
  },
  watch: {
    disabled: {
      immediate: true,
      handler(val) {
        this.rows.forEach(row => {
          row.fields.forEach(fieldRow => {
            fieldRow.props.disabled = fieldRow.field?.disabled || val;
          })
        })
      },
    },
    isCurrentStep() {
      this.rows.forEach(row => {
        row.fields.forEach(fieldRow => {
          if (fieldRow.props) fieldRow.props.locked = this.isFieldLocked(fieldRow.field);
        })
      })
    },
    layout: {
      immediate: true,
      deep: true,
      handler(layout) {
        this.rows = layout.map(row => this.prepRowDetails({ row }))
      }
    },
    values: {
      deep: true,
      immediate: true,
      handler(val) {
        dot.set(this.path, dot.pick(this.path, val), this.values);
        this.rows.forEach(row => {
          row.fields.forEach(fieldRow => {
            fieldRow.props.value = dot.pick(this.fieldValuePath(fieldRow.field, fieldRow.name), val) ?? ''
            if (fieldRow.field.disabledIf) {
              const check = fieldRow.field.disabledIf;
              const valToCheck = dot.pick(check.field, this.values);

              if (check.operator === 'NOT_EQUALS') {
                fieldRow.props.disabled = this.disabled || valToCheck !== check.value;
              }
              if (check.operator === 'EQUALS') {
                fieldRow.props.disabled = this.disabled || valToCheck === undefined || valToCheck === check.value;
              }
            }
            if (fieldRow.field.showIf) {
              fieldRow.props.hidden = !evaluateCondition(fieldRow.field.showIf, valuesLookup(this.values, this.record))
            }
            if (fieldRow.field.preselectedWith) {
              const check = fieldRow.field.preselectedWith;
              const valToCheck = dot.pick(check.field, this.values);
              fieldRow.props.preselected = valToCheck
            }
            if (fieldRow.field.computedFrom) {
              const { field: sourcePath, operation, factor, prefix, prefillOnly } = fieldRow.field.computedFrom
              // Support relative paths: if sourcePath has no dot, treat as sibling field in same repeater row
              const relPath = this.path ? `${this.path}.${sourcePath}` : sourcePath
              const sourceValue = dot.pick(relPath, val) ?? dot.pick(sourcePath, val)
              if (sourceValue == null || sourceValue === '') return
              const computedValue = this.applyOperation(sourceValue, operation, factor, prefix)
              if (computedValue === null) return
              const targetPath = this.fieldValuePath(fieldRow.field, fieldRow.name)
              if (prefillOnly && dot.pick(targetPath, val)) return
              fieldRow.props.value = computedValue
              if (dot.pick(targetPath, val) !== computedValue) {
                this.$emit('input', { name: targetPath, value: computedValue })
              }
            }
          })
        })
      },
    },
  },
  methods: {
    // A field may override the fieldset path; path: '' stores it at the root of values (e.g. root `category` inside a `process` fieldset).
    isFieldLocked(field) {
      return !!field?.locked || (!!field?.lockedUnlessCurrentStep && !this.isCurrentStep)
    },
    fieldValuePath(field, fieldName) {
      const basePath = field?.path ?? this.path
      return basePath ? `${basePath}.${fieldName}` : fieldName
    },
    getPropValue (prop) {
      return this[prop]?.[this.$i18n.locale] || this[prop] || ''
    },
    prepRowDetails({ row }) {
      const mappedRow = {
        columns: row.columns || '1fr 1fr 1fr',
        collapsible: !!this.collapsible,
        fields: row.fields.map(fieldName => {
          const field = this.fields[fieldName];

          return {
            name: fieldName,
            field: field,
            props: this.mapFieldProps(field, fieldName),
            readonly: this.fields[fieldName] ? (this.fields[fieldName].readonly || this.fields[fieldName].viewer || false) : false
          }
        })
      }

      return mappedRow
    },
    mapFieldProps(field, fieldName) {
      if (!field) return null

      const props = {
        type: field.type === 'string' ? 'text' : field.type,
        label: field.title?.[this.$i18n.locale] || field.title || '',
        value: dot.pick(this.fieldValuePath(field, fieldName), this.values) ?? '',
        options: field.options && this.setFieldOptions(field),
        validationRules: this.validation?.[fieldName] || {},
        disabled: field.disabled || this.disabled,
        preselected: field.preselected?.[this.$i18n.locale] || field.preselected,
        info: field.info?.[this.$i18n.locale] || field.info || field.description?.[this.$i18n.locale] || field.description || '',
        note: field.note?.[this.$i18n.locale] || field.note || '',
        placeholder: field.placeholder?.[this.$i18n.locale] || field.placeholder || '',
        preview: field.preview,
        prepend: field.prepend,
        append: field.append,
        rows: field.rows,
        min: field.min ?? null,
        max: field.max ?? null,
        formatter: field.formatter,
        hidden: field.hidden || false,
        locked: this.isFieldLocked(field)
      }

      if (field.fn) {
        if (dynamicFormHelpers[field.fn]) {
          props.options = dynamicFormHelpers[field.fn]()
        } else if (fn[field.fn]) {
          props.value = fn[field.fn]({ fieldValue: props.value, record: this.record })
        }
      }

      if (field.optionsFn) {
        const fieldOptionsFunc = dynamicFormHelpers[field.optionsFn] || fn[field.optionsFn]

        if (fieldOptionsFunc) {
          props.options = fieldOptionsFunc()
        }
      }

      return props
    },
    setFieldOptions (field) {
      field.options.map(option => {
       if (typeof option.text === 'object') {
        option.text = option.text[this.$i18n.locale] || option.text
       }

        return option
      })

      return [...field.options].sort((a, b) => a.text.localeCompare(b.text))
    },
    applyOperation(source, operation, factor, prefix) {
      switch (operation) {
        case 'multiply': return +source * factor
        case 'divide': return +source / factor
        case 'add': return +source + factor
        case 'subtract': return +source - factor
        case 'qrToEvse': return deriveEvse(source, prefix)
        case 'qrToEvseId': return deriveEvseId(source, prefix)
        case 'qrToEvseId2': return deriveEvseId2(source, prefix)
        default: return source
      }
    },
    handleInput({ name, value }) {
      this.$emit('input', {
        value,
        name,
      })
    },
    handleBlur({ name }) {
      this.$emit('blur', {
        name,
      })
    },
  },
}
</script>
